"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { formatNumber } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { getLocaleMeta } from "@/lib/i18n/locales";
import {
  type Decimal,
  decimalToString,
  trimDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  type ExpandedInput,
  type ExpandedStyle,
  expandedText,
  MAX_FRACTION_DIGITS,
  MAX_INTEGER_DIGITS,
  numberInWords,
  parseExpandedInput,
  placeDigits,
  placeInSentence,
  placeName,
  scientificParts,
  termValue,
} from "@/lib/tool-kit/arithmetic/expanded-form";
import {
  hasWordFormOptions,
  localizedWordForm,
} from "@/lib/tool-kit/arithmetic/expanded-form-words";
import { toSuperscript } from "@/lib/tool-kit/math/primes";

/** English strings. A translated content file overrides these through `ui`. */
export const EXPANDED_FORM_CALCULATOR_STRINGS = {
  numberLabel: "Number",
  numberHint:
    "A whole number or decimal like 4532 or 305.027 — or an expanded form such as 4000 + 500 + 30 + 2.",
  includeZeros: "Include the zero digits (0 × 100)",
  useAnd: "Use “and” in words (British style)",
  decimalWordsLabel: "Say decimals as",
  decimalsFractional: "Fractions: fifty-seven hundredths",
  decimalsPoint: "A point: point five seven",
  results: "Results",
  standardForm: "Standard form",
  scientific: "Scientific notation (called “standard form” in the UK)",
  wordForm: "Word form",
  copyWords: "Copy words",
  copyExpanded: "Copy expanded form",
  copied: "Copied!",
  formsHeading: "Written out",
  columnForm: "Form",
  columnWritten: "Written",
  formSum: "Expanded form",
  formProducts: "Digit × place value",
  formPowers: "Digit × power of ten",
  formFractions: "Digit × fraction",
  chartHeading: "Place-value chart",
  chartCaption: "Place-value chart for {number}",
  rowDigit: "Digit",
  rowValue: "Value",
  valuesHeading: "What each digit is worth",
  valueLine: "The {digit} in {place} place is worth {value}.",
  valueLineZero: "The 0 in {place} place holds no value.",
  fromSum: "Your expanded form adds up to {value}.",
  tooLong: "Use at most 36 digits before the decimal point and 30 after it.",
  enterNumber: "Enter a number, or an expanded form, to see it written out.",
  invalid: "That is not a number. Try 4532, 305.027 or 4000 + 500 + 30 + 2.",
  ones: [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ],
  tens: [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ],
  hundred: "hundred",
  scales: [
    "",
    "thousand",
    "million",
    "billion",
    "trillion",
    "quadrillion",
    "quintillion",
    "sextillion",
    "septillion",
    "octillion",
    "nonillion",
    "decillion",
  ],
  and: "and",
  point: "point",
  negative: "negative",
  zero: "zero",
  placeOnes: "ones",
  placeTens: "tens",
  placeHundreds: "hundreds",
  periodPlurals: [
    "",
    "thousands",
    "millions",
    "billions",
    "trillions",
    "quadrillions",
    "quintillions",
    "sextillions",
    "septillions",
    "octillions",
    "nonillions",
    "decillions",
  ],
  tenPrefix: "ten ",
  hundredPrefix: "hundred ",
  tenth: "tenth",
  hundredth: "hundredth",
  fractionBases: [
    "thousandth",
    "millionth",
    "billionth",
    "trillionth",
    "quadrillionth",
    "quintillionth",
    "sextillionth",
    "septillionth",
    "octillionth",
    "nonillionth",
    "decillionth",
  ],
  fractionTenPrefix: "ten-",
  fractionHundredPrefix: "hundred-",
  fractionPlural: "{unit}s",
  fractionDropEnding: "",
  /** Article before a place name in a sentence: ones, tens, hundreds, ten-…, hundred-…. */
  articleWhole: ["the ", "the ", "the ", "the ", "the "],
  /** Article before a period name, by index (index 0 is unused). */
  articlePeriods: [
    "",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
    "the ",
  ],
  articleFraction: "the ",
} as const;

type Strings = typeof EXPANDED_FORM_CALCULATOR_STRINGS;

const STYLES: readonly ExpandedStyle[] = [
  "sum",
  "products",
  "powers",
  "fractions",
];

function placeWords(strings: Strings) {
  return {
    ones: strings.placeOnes,
    tens: strings.placeTens,
    hundreds: strings.placeHundreds,
    periodPlurals: strings.periodPlurals,
    tenPrefix: strings.tenPrefix,
    hundredPrefix: strings.hundredPrefix,
    tenth: strings.tenth,
    hundredth: strings.hundredth,
    fractionBases: strings.fractionBases,
    fractionTenPrefix: strings.fractionTenPrefix,
    fractionHundredPrefix: strings.fractionHundredPrefix,
    fractionPlural: strings.fractionPlural,
    fractionDropEnding: strings.fractionDropEnding,
    articleWhole: strings.articleWhole,
    articlePeriods: strings.articlePeriods,
    articleFraction: strings.articleFraction,
  };
}

function groupedText(value: Decimal, locale: string, mark: string): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en-US";
  let whole = value.int;
  try {
    whole = BigInt(value.int).toLocaleString(tag);
  } catch {
    whole = value.int;
  }
  const trimmed = trimDecimal(value);
  const sign = value.negative ? "−" : "";
  return `${sign}${whole}${trimmed.frac === "" ? "" : `${mark}${trimmed.frac}`}`;
}

function fitsLimits(value: Decimal): boolean {
  return (
    value.int.length <= MAX_INTEGER_DIGITS &&
    value.frac.length <= MAX_FRACTION_DIGITS
  );
}

function FormsTable({
  value,
  includeZeros,
  mark,
  strings,
}: {
  value: Decimal;
  includeZeros: boolean;
  mark: string;
  strings: Strings;
}) {
  const labels: Record<ExpandedStyle, string> = {
    sum: strings.formSum,
    products: strings.formProducts,
    powers: strings.formPowers,
    fractions: strings.formFractions,
  };
  return (
    <DataTable
      head={[strings.columnForm, strings.columnWritten]}
      rows={STYLES.map((style) => [
        labels[style],
        expandedText(value, style, includeZeros, mark),
      ])}
    />
  );
}

function Chart({
  value,
  mark,
  strings,
}: {
  value: Decimal;
  mark: string;
  strings: Strings;
}) {
  const places = placeDigits(value);
  const words = placeWords(strings);
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="sr-only">
          {t(strings.chartCaption, { number: decimalToString(value) })}
        </caption>
        <thead>
          <tr>
            <td className={TD} />
            {places.map((place) => (
              <th
                className={`${TH} text-center ${place.exponent === -1 ? "border-l-2" : ""}`}
                key={place.exponent}
                scope="col"
              >
                {placeName(place.exponent, words)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th className={TH} scope="row">
              {strings.rowDigit}
            </th>
            {places.map((place) => (
              <td
                className={`${TD} text-center font-semibold text-lg ${place.exponent === -1 ? "border-l-2" : ""}`}
                key={place.exponent}
              >
                {place.digit}
              </td>
            ))}
          </tr>
          <tr>
            <th className={TH} scope="row">
              {strings.rowValue}
            </th>
            {places.map((place) => (
              <td
                className={`${TD} text-center ${place.exponent === -1 ? "border-l-2" : ""}`}
                key={place.exponent}
              >
                {termValue(place, mark)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * The English word form is assembled with English number-name grammar (space-joined
 * hundreds, hyphenated tens, "and"), which is wrong in most other languages. German,
 * French, Spanish and Chinese use their dedicated number-to-words modules; every other
 * locale shows no word form. The `ui` word arrays stay declared but go unused off-English.
 */
function wordFormText(
  value: Decimal,
  locale: string,
  options: { useAnd: boolean; decimals: "fractional" | "point" },
  strings: Strings
): string | null {
  if (hasWordFormOptions(locale)) {
    const words = placeWords(strings);
    return numberInWords(
      value,
      strings,
      { useAnd: options.useAnd, decimals: options.decimals },
      {
        singular: (exponent) => placeName(exponent, words, false),
        plural: (exponent) => placeName(exponent, words, true),
      }
    );
  }
  return localizedWordForm(value, locale);
}

function WordForm({ spoken, strings }: { spoken: string; strings: Strings }) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <p className="font-medium text-foreground text-sm">{strings.wordForm}</p>
      <p className="text-foreground">{spoken}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyWords}
        small
        text={spoken}
      />
    </div>
  );
}

function Results({
  input,
  options,
  strings,
}: {
  input: ExpandedInput;
  options: {
    includeZeros: boolean;
    useAnd: boolean;
    decimals: "fractional" | "point";
  };
  strings: Strings;
}) {
  const locale = useToolLocale();
  const mark = formatNumber(locale, 1.5).charAt(1);
  const value = trimDecimal(input.value);
  const words = placeWords(strings);
  const spoken = wordFormText(value, locale, options, strings);
  const sci = scientificParts(value);
  const sum = expandedText(value, "sum", options.includeZeros, mark);
  const everyDigit = options.includeZeros || sum === "0";
  const lines = placeDigits(value)
    .filter((place) => everyDigit || place.digit !== 0)
    .map((place) => ({
      key: place.exponent,
      text:
        place.digit === 0
          ? t(strings.valueLineZero, {
              place: placeInSentence(place.exponent, words),
            })
          : t(strings.valueLine, {
              digit: place.digit,
              place: placeInSentence(place.exponent, words),
              value: termValue(place, mark),
            }),
    }));
  return (
    <>
      {input.fromSum ? (
        <Notice>
          {t(strings.fromSum, { value: groupedText(value, locale, mark) })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.standardForm}
          value={groupedText(value, locale, mark)}
        />
        {sci ? (
          <StatCard
            label={strings.scientific}
            value={`${sci.mantissa.replace(".", mark)} × 10${toSuperscript(sci.exponent)}`}
          />
        ) : null}
      </StatGrid>
      {spoken === null ? null : <WordForm spoken={spoken} strings={strings} />}
      <h3 className={WIDGET_H3}>{strings.formsHeading}</h3>
      <FormsTable
        includeZeros={options.includeZeros}
        mark={mark}
        strings={strings}
        value={value}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyExpanded}
        small
        text={`${decimalToString(value)} = ${sum}`}
      />
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <Chart mark={mark} strings={strings} value={value} />
      <h3 className={WIDGET_H3}>{strings.valuesHeading}</h3>
      <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
        {lines.map((line) => (
          <li key={line.key}>{line.text}</li>
        ))}
      </ul>
    </>
  );
}

export default function ExpandedFormCalculatorWidget() {
  const strings = useToolUi(EXPANDED_FORM_CALCULATOR_STRINGS);
  const wordForm = hasWordFormOptions(useToolLocale());
  const [raw, setRaw] = useState("4532.07");
  const [includeZeros, setIncludeZeros] = useState(false);
  const [useAnd, setUseAnd] = useState(false);
  const [decimals, setDecimals] = useState<"fractional" | "point">(
    "fractional"
  );
  const input = useMemo(
    () => (raw.trim() === "" ? null : parseExpandedInput(raw)),
    [raw]
  );
  let body = <Notice>{strings.enterNumber}</Notice>;
  if (raw.trim() !== "" && input === null) {
    body = <ErrorNotice>{strings.invalid}</ErrorNotice>;
  } else if (input && !fitsLimits(trimDecimal(input.value))) {
    body = <ErrorNotice>{strings.tooLong}</ErrorNotice>;
  } else if (input) {
    body = (
      <Results
        input={input}
        options={{ includeZeros, useAnd, decimals }}
        strings={strings}
      />
    );
  }
  return (
    <>
      <TextField
        hint={strings.numberHint}
        id="ef-number"
        label={strings.numberLabel}
        mono
        numeric
        onChange={setRaw}
        value={raw}
      />
      <div
        className={`grid gap-4 ${wordForm ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}
      >
        <div className="self-end">
          <Checkbox
            checked={includeZeros}
            label={strings.includeZeros}
            onChange={setIncludeZeros}
          />
        </div>
        {wordForm ? (
          <>
            <div className="self-end">
              <Checkbox
                checked={useAnd}
                label={strings.useAnd}
                onChange={setUseAnd}
              />
            </div>
            <SelectField
              id="ef-decimals"
              label={strings.decimalWordsLabel}
              onChange={setDecimals}
              options={[
                { value: "fractional", label: strings.decimalsFractional },
                { value: "point", label: strings.decimalsPoint },
              ]}
              value={decimals}
            />
          </>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
